import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from "rxjs";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {Store} from "@ngrx/store";
import {AppState} from "../../../store/reducers";
import {Router} from "@angular/router";
import {CitizenTypeInum} from "../../../store/entities/citizens/register/citizens/citizens.model";
import {createCitizenMutation} from "../../../store/entities/citizens/register/citizens/citizens.actions";
import {NotificationService} from "../../../services/notification.service";
import {
  EMAIL_VALIDATOR,
  PASSPORT_VALIDATOR
} from "../../../shared/components/dynamic-forms-components/custom-validators";
import {passwordPolicyValidator} from "../../../shared/validators/input-validators";

@Component({
  selector: 'app-foreigner-registration',
  templateUrl: './foreigner-registration.component.html',
  styleUrl: './foreigner-registration.component.scss'
})
export class ForeignerRegistrationComponent implements OnInit, OnDestroy {
  form: FormGroup;
  saveLoading = false;
  subscription$: Subscription = new Subscription();
  preferredCountries: string[] = ['tz', 'ke', 'ug'];

  hidePassword = true;
  hideConfirmPassword = true;

  constructor(
    private fb: FormBuilder,
    private store: Store<AppState>,
    private router: Router,
    private notificationService: NotificationService
  ) {
  }

  ngOnInit(): void {
    this.form = this.fb.group(
      {
        userFirstName: ['', Validators.required],
        userLastName: ['', Validators.required],
        passportNumber: ['', [Validators.required, PASSPORT_VALIDATOR.validator]],
        profilePhone: [null, [this.validPhoneValidator()]],
        userEmail: ['', [Validators.required, EMAIL_VALIDATOR.validator]],
        userPassword: ['', [Validators.required, passwordPolicyValidator()]],
        confirmPassword: ['', [Validators.required, passwordPolicyValidator()]],
        consent: [false, Validators.requiredTrue],
      },
      {validator: this.matchPasswords('userPassword', 'confirmPassword')}
    );
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  async submitForm() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const input = this.form.value;

    const formData = {
      citizenType: CitizenTypeInum.Foreigner,
      userPassword: input.userPassword,
      userFirstName: input.userFirstName,
      userLastName: input.userLastName,
      userEmail: input.userEmail,
      profilePhone: input.profilePhone?.internationalNumber,
    };

    this.store.dispatch(createCitizenMutation({input: formData}));
  }

  matchPasswords(passwordKey: string, confirmPasswordKey: string) {
    return (formGroup: FormGroup) => {
      const password = formGroup.get(passwordKey);
      const confirmPassword = formGroup.get(confirmPasswordKey);

      if (!password || !confirmPassword) return null;

      if (confirmPassword.errors && !confirmPassword.errors['passwordMismatch']) {
        return null;
      }

      if (password.value !== confirmPassword.value) {
        confirmPassword.setErrors({passwordMismatch: true});
      } else {
        confirmPassword.setErrors(null);
      }
      return null;
    };
  }

  validPhoneValidator(): Validators {
    return (control) => {
      if (!control.value || !control.value.internationalNumber) {
        return {required: true};
      }
      return null;
    };
  }

  privacyPolicyText: string = `
  With my consent, I agree to the collection, use, and storage of my personal information in accordance with the
  <a href="/privacy-policy" target="_blank" class="underline text-primary-600">Privacy Policy</a>.
  Your information will be securely stored and used only for the stated purposes.
  You have the right to access and update your information at any time.
`;
}
